import {
  changeComplaintStatus,
  getAdminComplaints,
} from "../../../Redux/Assets/AssetsActions";
import ConfirmationModal from "../../../Components/Common/ConfirmationModal/ConfirmationModal";
import { ColDef, GridReadyEvent, ICellRendererParams } from "ag-grid-community";
import { hasAdminPermission } from "../../../Util/adminPermissions";
import Loading from "../../../Components/Common/Loading/Loading";
import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import { AppDispatch, RootState } from "../../../Redux/store";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { HiDotsVertical } from "react-icons/hi";
import { IoCloseSharp } from "react-icons/io5";
import { AgGridReact } from "ag-grid-react";
import styles from "./Tickets.module.scss";
import moment from "moment";

interface TicketType {
  id: number;
  comments: string;
  status: number;
  statusName: string;
  createdBy: number;
  createdByName: string;
  createdAt: string;
  modifiedBy: number | null;
  modifiedAt: string | null;
  typeId: number;
  typeName: string;
  amenityId: number | null;
  amenityName: string | null;
  assetId: number | null;
  assetName: string | null;
}

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  wrapText: true,
  autoHeight: true,
  unSortIcon: true,
};

function Tickets() {
  const dispatch = useDispatch<AppDispatch>();
  const {
    adminComplaintsData,
    adminComplaintsLoading,
    changeComplaintStatusLoading,
  } = useSelector((state: RootState) => state.assets);
  const { adminPermissions } = useSelector((state: RootState) => state.auth);
  const canManage = hasAdminPermission(adminPermissions, "tickets_manage");
  const [rowData, setRowData] = useState<TicketType[]>([]);
  const [gridApi, setGridApi] = useState<any>(null);
  const [search, setSearch] = useState("");
  const [resolveTicketId, setResolveTicketId] = useState<number>(-1);
  const [
    resolveTicketConfirmationModalOpen,
    setResolveTicketConfirmationModalOpen,
  ] = useState(false);

  const filteredRowData = rowData?.filter((item) => {
    const searchValue = search?.toLowerCase();
    if (!searchValue) return true;

    return (
      item?.amenityName?.toLowerCase()?.includes(searchValue) ||
      item?.createdByName?.toLowerCase()?.includes(searchValue) ||
      item?.typeName?.toLowerCase()?.includes(searchValue) ||
      item?.assetName?.toLowerCase()?.includes(searchValue) ||
      item?.statusName?.toLowerCase()?.includes(searchValue)
    );
  });

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params?.api);
    params?.api?.sizeColumnsToFit();
  };

  const resolveTicketHandler = (id: number) => {
    if (!changeComplaintStatusLoading && id) {
      const successHandler = () => {
        setResolveTicketConfirmationModalOpen(false);
        setResolveTicketId(-1);
        dispatch(getAdminComplaints());
      };
      dispatch(
        changeComplaintStatus({
          complaintId: id,
          successHandler,
        }),
      );
    }
  };

  const columnDefs: ColDef<TicketType>[] = [
    {
      headerName: "Type",
      field: "typeName",
      flex: 1,
      minWidth: 120,
    },
    {
      headerName: "Amenity",
      field: "amenityName",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params?.data?.amenityName || "N/A",
    },
    {
      headerName: "Asset",
      field: "assetName",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => params?.data?.assetName || "N/A",
    },
    {
      headerName: "Comments",
      field: "comments",
      flex: 1,
      minWidth: 200,
    },
    {
      headerName: "Created By",
      field: "createdByName",
      flex: 1,
      minWidth: 150,
    },
    {
      headerName: "Status",
      field: "statusName",
      flex: 1,
      minWidth: 120,
      cellClass: (params) => {
        const status = params?.data?.statusName?.toLowerCase();
        if (status === "pending") return "expiredText";
        if (status === "solved") return "activeText";
        return "";
      },
    },
    {
      headerName: "Created At",
      field: "createdAt",
      flex: 1,
      minWidth: 140,
      valueGetter: (params) => {
        return params?.data?.createdAt
          ? moment(params.data.createdAt).format("MM/DD/YYYY")
          : "N/A";
      },
    },
    ...(canManage
      ? [
          {
            width: 20,
            colId: "actions",
            sortable: false,
            suppressAutoSize: true,
            suppressMovable: true,
            suppressNavigable: true,
            editable: false,
            cellStyle: {
              pointerEvents: "all",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            },
            cellRenderer: (params: ICellRendererParams<TicketType>) => {
              if (params?.data?.statusName?.toLowerCase() !== "pending") {
                return null;
              }

              return (
                <div className={styles.iconContainer}>
                  <Menu
                    direction="left"
                    menuButton={
                      <MenuButton>
                        <HiDotsVertical className={styles.icon} />
                      </MenuButton>
                    }
                    transition
                    portal
                  >
                    <MenuItem
                      onClick={() => {
                        setResolveTicketId(params?.data?.id || -1);
                        setResolveTicketConfirmationModalOpen(true);
                      }}
                    >
                      Mark as Resolved
                    </MenuItem>
                  </Menu>
                </div>
              );
            },
          } as ColDef<TicketType>,
        ]
      : []),
  ];

  useEffect(() => {
    if (!gridApi) return;

    const handleResize = () => {
      gridApi?.sizeColumnsToFit();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [gridApi]);

  useEffect(() => {
    dispatch(getAdminComplaints());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (adminComplaintsData) {
      setRowData(adminComplaintsData);
    }
  }, [adminComplaintsData]);

  return (
    <React.Fragment>
      {(adminComplaintsLoading || changeComplaintStatusLoading) && <Loading />}
      <div className={styles.mainContainer}>
        <div className={styles.topContainer}>
          <div className={styles.headingContainer}>
            <h1 className={styles.heading}>Tickets</h1>
          </div>
          <div className={styles.buttonContainer}></div>
        </div>
        <div className={styles.bodyContainer}>
          <div className={styles.searchContainer}>
            <input
              type="text"
              className={styles.input}
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e?.target?.value || "")}
            />
            <div className={styles.iconContainer} onClick={() => setSearch("")}>
              <IoCloseSharp className={styles.icon} />
            </div>
          </div>

          <div className={styles.gridContainer}>
            <AgGridReact
              className={styles.grid}
              rowData={filteredRowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={true}
              paginationPageSize={10}
              paginationPageSizeSelector={[5, 10, 20, 50, 100]}
              animateRows={true}
              domLayout="autoHeight"
            />
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={resolveTicketConfirmationModalOpen}
        setIsOpen={setResolveTicketConfirmationModalOpen}
        onConfirm={() => resolveTicketHandler(resolveTicketId)}
        heading="Resolve Ticket"
        text="Are you sure you want to mark this ticket as resolved?"
        loading={changeComplaintStatusLoading}
      />
    </React.Fragment>
  );
}

export default Tickets;
